import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('crm_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('crm_token');
      localStorage.removeItem('crm_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
};

// Clients API
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getPhoneCollisions: () => api.get('/clients/phone-collisions'),
  scanDuplicates: () => api.get('/clients/duplicate-scan'),
  cleanupDuplicates: () => api.post('/clients/cleanup-duplicates'),
};

// Follow-ups API
export const followUpsAPI = {
  getAll: (completed) => api.get('/follow-ups', { params: { completed } }),
  create: (data) => api.post('/follow-ups', data),
  update: (id, data) => api.put(`/follow-ups/${id}`, data),
  delete: (id) => api.delete(`/follow-ups/${id}`),
};

// Potential Customers API
export const potentialCustomersAPI = {
  getAll: () => api.get('/potential-customers'),
  create: (data) => api.post('/potential-customers', data),
  update: (id, data) => api.put(`/potential-customers/${id}`, data),
  delete: (id) => api.delete(`/potential-customers/${id}`),
};

// Coupons API
export const couponsAPI = {
  getAll: () => api.get('/coupons'),
  create: (data) => api.post('/coupons', data),
  delete: (id) => api.delete(`/coupons/${id}`),
};

// Team API
export const teamAPI = {
  getAll: () => api.get('/team'),
  create: (data) => api.post('/team', data),
};

// Stats API
export const statsAPI = {
  get: () => api.get('/stats'),
};

// WooCommerce Stores API
export const wooStoresAPI = {
  getAll: () => api.get('/woo-stores'),
  create: (data) => api.post('/woo-stores', data),
  update: (id, data) => api.put(`/woo-stores/${id}`, data),
  delete: (id) => api.delete(`/woo-stores/${id}`),
  testConnection: (id) => api.post(`/woo-stores/${id}/test`),
  sync: (id) => api.post(`/woo-stores/${id}/sync`),
  recalculateStats: (id) => api.post(`/woo-stores/${id}/recalculate-stats`),
  getSyncHistory: () => api.get('/sync-history'),
  getSyncIssues: (page = 1, issueType = '') => api.get(`/sync-issues?page=${page}&per_page=50${issueType ? `&issue_type=${issueType}` : ''}`),
  getSyncStatus: () => api.get('/sync-status'),
  getStoreSyncStatus: (id) => api.get(`/sync-status/${id}`),
  getSyncProgress: (id) => api.get(`/woo-stores/${id}/sync-progress`),
};

// WhatsApp Broadcast API
export const waAPI = {
  // Dashboard
  getDashboard: () => api.get('/wa/dashboard'),
  
  // Templates
  getTemplates: () => api.get('/wa/templates'),
  createTemplate: (data) => api.post('/wa/templates', data),
  updateTemplate: (id, data) => api.put(`/wa/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/wa/templates/${id}`),
  
  // Groups
  getGroups: () => api.get('/wa/groups'),
  getGroup: (id) => api.get(`/wa/groups/${id}`),
  createGroup: (data) => api.post('/wa/groups', data),
  updateGroup: (id, data) => api.put(`/wa/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/wa/groups/${id}`),
  
  // Group Members
  getGroupMembers: (groupId) => api.get(`/wa/groups/${groupId}/members`),
  addGroupMembers: (groupId, data) => api.post(`/wa/groups/${groupId}/members`, data),
  removeGroupMembers: (groupId, data) => api.delete(`/wa/groups/${groupId}/members`, { data }),
  
  // Campaigns
  getCampaigns: (params) => api.get('/wa/campaigns', { params }),
  getCampaign: (id) => api.get(`/wa/campaigns/${id}`),
  createCampaign: (data) => api.post('/wa/campaigns', data),
  updateCampaign: (id, data) => api.put(`/wa/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/wa/campaigns/${id}`),
  startCampaign: (id) => api.post(`/wa/campaigns/${id}/start`),
  pauseCampaign: (id) => api.post(`/wa/campaigns/${id}/pause`),
  resumeCampaign: (id) => api.post(`/wa/campaigns/${id}/resume`),
  
  // Scheduled Campaigns
  getScheduledCampaigns: () => api.get('/wa/scheduled-campaigns'),
  processScheduledCampaigns: () => api.post('/wa/process-scheduled-campaigns'),
  
  // Send Queue
  getQueue: (campaignId, params) => api.get(`/wa/campaigns/${campaignId}/queue`, { params }),
  markSent: (campaignId, messageId) => api.post(`/wa/campaigns/${campaignId}/queue/${messageId}/sent`),
  markFailed: (campaignId, messageId, errorNote) => 
    api.post(`/wa/campaigns/${campaignId}/queue/${messageId}/failed`, null, { params: { error_note: errorNote } }),
  skipMessage: (campaignId, messageId) => api.post(`/wa/campaigns/${campaignId}/queue/${messageId}/skip`),
  retryMessage: (campaignId, messageId) => api.post(`/wa/campaigns/${campaignId}/queue/${messageId}/retry`),
  
  // Add clients/groups to campaigns
  addClientsToCampaign: (campaignId, clientIds) => api.post(`/wa/campaigns/${campaignId}/add-clients`, { client_ids: clientIds }),
  addGroupToCampaign: (campaignId, groupId) => api.post(`/wa/campaigns/${campaignId}/add-group`, { group_id: groupId }),
  getCampaignRecipients: (campaignId) => api.get(`/wa/campaigns/${campaignId}/recipients`),
  
  // Fixed Flow Campaigns
  getFixedFlows: () => api.get('/wa/fixed-flows'),
  getFixedFlow: (id) => api.get(`/wa/fixed-flows/${id}`),
  createFixedFlow: (data) => api.post('/wa/fixed-flows', data),
  updateFixedFlow: (id, data) => api.put(`/wa/fixed-flows/${id}`, data),
  deleteFixedFlow: (id) => api.delete(`/wa/fixed-flows/${id}`),
  
  // Fixed Flow Entries
  getFixedFlowEntries: (flowId, status) => api.get(`/wa/fixed-flows/${flowId}/entries`, { params: { status } }),
  addClientsToFlow: (flowId, clientIds) => api.post(`/wa/fixed-flows/${flowId}/entries`, { client_ids: clientIds }),
  addGroupToFlow: (flowId, groupId) => api.post(`/wa/fixed-flows/${flowId}/entries/from-group`, { group_id: groupId }),
  getFixedFlowQueue: (flowId) => api.get(`/wa/fixed-flows/${flowId}/queue`),
  markFlowMessageSent: (flowId, entryId) => api.post(`/wa/fixed-flows/${flowId}/entries/${entryId}/send`),
  skipFlowMessage: (flowId, entryId, reason = '') => api.post(`/wa/fixed-flows/${flowId}/entries/${entryId}/skip`, { reason }),
  stopFlowForClient: (flowId, entryId) => api.post(`/wa/fixed-flows/${flowId}/entries/${entryId}/stop`),
  autoEnrollNewClients: () => api.post('/wa/fixed-flows/auto-enroll'),
};

export default api;
